import React, { useEffect } from 'react';
import './ContextMenu.css';

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  onDownloadModelOutput?: () => void;
  onDownloadStartState?: () => void;
  onCompareVideos?: () => void;
  software?: string;
  showVideoComparison?: boolean;
}

const DESKTOP_SOFTWARE = new Set([
  'freecad',
  'blender',
  'musescore',
  'kicad',
  'audacity',
  'gimp',
  'shotcut',
  'inkscape',
  'libreoffice',
]);

const ContextMenu: React.FC<ContextMenuProps> = ({
  x,
  y,
  onClose,
  onDownloadModelOutput,
  onDownloadStartState,
  onCompareVideos,
  software,
  showVideoComparison,
}) => {
  useEffect(() => {
    const handleClickOutside = () => onClose();
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [onClose]);

  const softwareKey = (software || '').toLowerCase();
  const isDesktopSoftware = DESKTOP_SOFTWARE.has(softwareKey);
  const canShowCompare = showVideoComparison && Boolean(onCompareVideos);

  const getOutputLabel = () => {
    switch (softwareKey) {
      case 'freecad':
        return 'Download FreeCAD File (.FCStd)';
      case 'blender':
        return 'Download Blender File (.blend)';
      case 'musescore':
        return 'Download MuseScore File (.mscz)';
      case 'kicad':
        return 'Download AI output (.zip)';
      case 'immich':
        return 'Download Immich Export (.zip)';
      case 'odoo':
        return 'Download Odoo Export (.zip)';
      case 'erpnext':
        return 'Download ERPNext Export (.zip)';
      case 'audacity':
        return 'Download AI output (.zip)';
      case 'gimp':
        return 'Download AI output (.zip)';
      case 'shotcut':
        return 'Download AI output (.zip)';
      case 'inkscape':
        return 'Download AI output (.zip)';
      case 'libreoffice':
        return 'Download AI output (.zip)';
      case 'mattermost':
        return 'Download Mattermost Export (.zip)';
      default:
        return 'Download Model Output';
    }
  };

  const renderDesktopActions = () => (
    <>
      {onDownloadModelOutput && (
        <button className="context-menu-item" onClick={onDownloadModelOutput}>
          <svg width="16" height="16" viewBox="0 0 16 16" className="context-menu-icon">
            <path
              d="M8 12l-4-4h2.5V3h3v5H12l-4 4zm-6 1v2h12v-2H2z"
              fill="currentColor"
            />
          </svg>
          {getOutputLabel()}
        </button>
      )}

      {onDownloadStartState && (
        <button className="context-menu-item" onClick={onDownloadStartState}>
          <svg width="16" height="16" viewBox="0 0 16 16" className="context-menu-icon">
            <path
              d="M8 12l-4-4h2.5V3h3v5H12l-4 4zm-6 1v2h12v-2H2z"
              fill="currentColor"
            />
          </svg>
          Download Start State
        </button>
      )}

      {canShowCompare && (
        <button className="context-menu-item" onClick={onCompareVideos}>
          <svg width="16" height="16" viewBox="0 0 16 16" className="context-menu-icon">
            <path
              d="M2 2v12h5V2H2zm1 11V3h3v10H3zm6-11v12h5V2H9zm1 11V3h3v10h-3z"
              fill="currentColor"
            />
          </svg>
          Compare Files Side-by-Side
        </button>
      )}
    </>
  );

  return (
    <div className="context-menu" style={{ left: x, top: y }}>
      {isDesktopSoftware ? (
        renderDesktopActions()
      ) : (
        <>
          {onDownloadModelOutput && (
            <button className="context-menu-item" onClick={onDownloadModelOutput}>
              <svg width="16" height="16" viewBox="0 0 16 16" className="context-menu-icon">
                <path
                  d="M8 12l-4-4h2.5V3h3v5H12l-4 4zm-6 1v2h12v-2H2z"
                  fill="currentColor"
                />
              </svg>
              Download Model Output
            </button>
          )}
          {canShowCompare && (
            <button className="context-menu-item" onClick={onCompareVideos}>
              <svg width="16" height="16" viewBox="0 0 16 16" className="context-menu-icon">
                <path
                  d="M2 2v12h5V2H2zm1 11V3h3v10H3zm6-11v12h5V2H9zm1 11V3h3v10h-3z"
                  fill="currentColor"
                />
              </svg>
              Compare Files Side-by-Side
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default ContextMenu;
